import {registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {NgModule} from '@angular/core';
import {AngularFireModule} from '@angular/fire/compat';
import {AngularFireMessagingModule, SERVICE_WORKER} from '@angular/fire/compat/messaging';
import {FormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {
  FacebookLoginProvider,
  GoogleLoginProvider,
  SocialAuthServiceConfig,
  SocialLoginModule,
} from 'angularx-social-login';
import {MathjaxModule} from 'mathjax-angular';
import {NzAvatarModule} from 'ng-zorro-antd/avatar';
import {NzBadgeModule} from 'ng-zorro-antd/badge';
import {NzBreadCrumbModule} from 'ng-zorro-antd/breadcrumb';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzEmptyModule} from 'ng-zorro-antd/empty';
import {NZ_DATE_CONFIG, NZ_DATE_LOCALE, NZ_I18N, vi_VN, en_US} from 'ng-zorro-antd/i18n';
import vi from '@angular/common/locales/vi';
import en from '@angular/common/locales/en';
import {enUS, vi as viVN} from 'date-fns/locale';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzLayoutModule} from 'ng-zorro-antd/layout';
import {NzMenuModule} from 'ng-zorro-antd/menu';
import {NzPopoverModule} from 'ng-zorro-antd/popover';
import {NzSwitchModule} from 'ng-zorro-antd/switch';
import {SocketIoConfig, SocketIoModule} from 'ngx-socket-io';
import {environment} from 'src/environments/environment';
import {GoogleTagManagerModule} from 'angular-google-tag-manager';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {AppHeaderComponent} from './main-layout/app-header/app-header.component';
import {MainLayoutComponent} from './main-layout/main-layout.component';
import {SidebarComponent} from './main-layout/sidebar/sidebar.component';
import {AuthInterceptor} from './shared/interceptors/auth.interceptor';
import {SharedModule} from './shared/shared.module';
import {TranslocoRootModule} from './transloco-root.module';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from "ng-recaptcha";
import { UserHttpInterceptor } from "./shared/interceptors/user-http.interceptor";

registerLocaleData(localStorage.getItem('student_lang') === 'en' ? en : vi);

const NzModules = [
  NzLayoutModule,
  NzIconModule,
  NzMenuModule,
  NzBreadCrumbModule,
  NzInputModule,
  NzBadgeModule,
  NzAvatarModule,
  NzSwitchModule,
  NzButtonModule,
  NzPopoverModule,
  NzEmptyModule,
];

const HttpInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: UserHttpInterceptor, multi: true },
];

const socketIoConfig: SocketIoConfig = {
  url: environment.socketUrl,
  options: {
    path: '/student.io'
  }
};

const fbLoginOptions = {
  scope: 'public_profile email',
  return_scopes: true,
  enable_profile_selector: true,
  auth_type: 'rerequest'
}; // https://developers.facebook.com/docs/reference/javascript/FB.login/v16.0

const googleLoginOptions = {
  apiKey: environment.ggApiKey,
  scope: 'profile email',
  plugin_name: 'login'
}; // https://developers.google.com/api-client-library/javascript/reference/referencedocs#gapiauth2clientconfig

@NgModule({
  declarations: [
    AppComponent,
    MainLayoutComponent,
    AppHeaderComponent,
    SidebarComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    SharedModule,
    NzModules,
    TranslocoRootModule,
    MathjaxModule.forRoot(
      {
        "config": {
          "loader": {
            "load": ["output/svg", "[tex]/require", "[tex]/ams"]
          },
          "tex": {
            "inlineMath": [['$', '$'], ['\\(', '\\)']],
            "packages": ["base", "require", "ams"]
          },
          "svg": { "fontCache": "global" }
        }
      }
    ),
    SocketIoModule.forRoot(socketIoConfig),
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireMessagingModule,
    SocialLoginModule,
    GoogleTagManagerModule.forRoot({
      id: environment.GTM_ID,
    }),
    RecaptchaV3Module
  ],
  providers: [
    { provide: NZ_I18N, useValue: localStorage.getItem('student_lang') === 'en' ? en_US : vi_VN },
    { provide: NZ_DATE_CONFIG, useValue: { firstDayOfWeek: 1 } },
    { provide: NZ_DATE_LOCALE, useValue: localStorage.getItem('student_lang') === 'en' ? enUS : viVN },
    HttpInterceptorProviders,
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(environment.ggClientId, googleLoginOptions)
          },
          {
            id: FacebookLoginProvider.PROVIDER_ID,
            provider: new FacebookLoginProvider(environment.fbClientId, fbLoginOptions)
          }
        ]
      } as SocialAuthServiceConfig,
    },
    {
      provide: SERVICE_WORKER,
      useFactory: () => typeof navigator !== 'undefined' && navigator.serviceWorker?.register(environment.fcmConfig.fcmSw, {}) || undefined
    },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaSiteKey },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
